import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { api } from '@carrent/api'
import { Icon } from '@carrent/ui'
import { DashboardGrid } from './DashboardGrid'
import { DashboardSettingsModal } from './DashboardSettings'
import { DisabledTray } from './DisabledTray'
import { WidgetGallery } from './WidgetGallery'
import { WidgetSettingsModal } from './WidgetSettings'
import { useWidgetData } from './useWidgetData'

/**
 * Информационная панель: конструктор из гаджетов.
 *
 * Существующий дашборд (/admin) остаётся нетронутым — это отдельный
 * инструмент, а не замена.
 */

const SAVE_DEBOUNCE_MS = 800

function newId() {
  return Math.random().toString(36).slice(2, 14)
}

function relativeTime(date) {
  if (!date) return null
  const sec = Math.round((Date.now() - date.getTime()) / 1000)
  if (sec < 10) return 'только что'
  if (sec < 60) return `${sec} сек назад`
  const min = Math.round(sec / 60)
  if (min < 60) return `${min} мин назад`
  return `${Math.round(min / 60)} ч назад`
}

export default function InfoPanelPage() {
  const [boards, setBoards] = useState([])
  const [activeId, setActiveId] = useState(null)
  const [catalogItems, setCatalogItems] = useState([])
  const [presets, setPresets] = useState([])
  const [canManageCatalog, setCanManageCatalog] = useState(false)
  const [loadError, setLoadError] = useState(null)
  const [booting, setBooting] = useState(true)

  const [galleryOpen, setGalleryOpen] = useState(false)
  const [creating, setCreating] = useState(false)
  const [settingsOpen, setSettingsOpen] = useState(false)
  const [editingWidget, setEditingWidget] = useState(null)
  const [, setTick] = useState(0)

  const saveTimer = useRef(null)

  const board = boards.find((b) => b.id === activeId) || null
  const layout = board?.layout || []
  const canEdit = Boolean(board?.can_edit)

  const catalogByType = useMemo(
    () => Object.fromEntries(catalogItems.map((i) => [i.type, i])),
    [catalogItems],
  )

  const { results, loading, error, updatedAt, refresh } = useWidgetData(
    layout,
    board?.refresh_interval_sec ?? 0,
  )

  // ── Загрузка панелей и каталога ───────────────────────────────────────────
  useEffect(() => {
    let cancelled = false
    Promise.all([
      api.get('/admin/dashboards'),
      api.get('/admin/dashboards/catalog'),
    ])
      .then(([boardsRes, catalogRes]) => {
        if (cancelled) return
        const items = boardsRes.data.data.items
        setBoards(items)
        setActiveId((cur) => cur ?? (items.find((b) => b.is_default) || items[0])?.id ?? null)

        const cat = catalogRes.data.data
        setCatalogItems(cat.items)
        setPresets(cat.presets || [])
        setCanManageCatalog(cat.can_manage)
        setLoadError(null)
      })
      .catch(() => {
        if (!cancelled) setLoadError('Панели не загрузились')
      })
      .finally(() => {
        if (!cancelled) setBooting(false)
      })
    return () => {
      cancelled = true
    }
  }, [])

  // Подпись «обновлено N назад» должна стареть сама, иначе она врёт.
  // Значение счётчика не используется — важен сам факт перерисовки.
  useEffect(() => {
    const t = setInterval(() => setTick((v) => v + 1), 15000)
    return () => clearInterval(t)
  }, [])

  // ── Сохранение раскладки ──────────────────────────────────────────────────
  const persist = useCallback((boardId, patch) => {
    if (saveTimer.current) clearTimeout(saveTimer.current)
    saveTimer.current = setTimeout(() => {
      api.patch(`/admin/dashboards/${boardId}`, patch).catch(() => {
        setLoadError('Изменения не сохранились')
      })
    }, SAVE_DEBOUNCE_MS)
  }, [])

  useEffect(() => () => saveTimer.current && clearTimeout(saveTimer.current), [])

  const updateLayout = useCallback(
    (nextLayout) => {
      if (!board) return
      setBoards((bs) =>
        bs.map((b) => (b.id === board.id ? { ...b, layout: nextLayout } : b)),
      )
      persist(board.id, { layout: nextLayout })
    },
    [board, persist],
  )

  // ── Действия ──────────────────────────────────────────────────────────────
  const addWidget = (item) => {
    updateLayout([
      ...layout,
      {
        id: newId(),
        type: item.type,
        size: item.default_size,
        enabled: true,
        settings: item.default_settings || {},
      },
    ])
    setGalleryOpen(false)
  }

  const createBoard = async (preset) => {
    setCreating(false)
    try {
      const { data } = await api.post('/admin/dashboards', preset ? { preset } : {})
      setBoards((bs) => [...bs, data.data])
      setActiveId(data.data.id)
    } catch {
      setLoadError('Панель не создалась')
    }
  }

  const cloneBoard = async () => {
    try {
      const { data } = await api.post(`/admin/dashboards/${board.id}/clone`)
      setBoards((bs) => [...bs, data.data])
      setActiveId(data.data.id)
    } catch {
      setLoadError('Копия не создалась')
    }
  }

  const saveBoardSettings = async (patch) => {
    try {
      const { data } = await api.patch(`/admin/dashboards/${board.id}`, patch)
      setBoards((bs) => bs.map((b) => (b.id === board.id ? data.data : b)))
    } catch {
      setLoadError('Настройки не сохранились')
    }
  }

  const saveCatalog = async (items) => {
    try {
      await api.patch('/admin/dashboards/catalog', { items })
      const { data } = await api.get('/admin/dashboards/catalog')
      setCatalogItems(data.data.items)
      refresh()
    } catch {
      setLoadError('Каталог не сохранился')
    }
  }

  const deleteBoard = async () => {
    try {
      await api.delete(`/admin/dashboards/${board.id}`)
      const rest = boards.filter((b) => b.id !== board.id)
      setBoards(rest)
      setActiveId(rest[0]?.id ?? null)
      setSettingsOpen(false)
    } catch {
      setLoadError('Панель не удалилась')
    }
  }

  // ── Состояния страницы ────────────────────────────────────────────────────
  if (booting) {
    return (
      <div className="full-page-center">
        <div className="spinner" />
      </div>
    )
  }

  if (loadError && !board) {
    return (
      <div className="panel-empty">
        <p className="panel-empty__title">{loadError}</p>
        <button
          type="button"
          className="btn btn--primary"
          onClick={() => globalThis.location.reload()}
        >
          Обновить страницу
        </button>
      </div>
    )
  }

  if (!board) {
    return (
      <div className="panel-empty">
        <p className="panel-empty__title">Панелей пока нет</p>
        <p className="panel-empty__hint">Начните с шаблона — его можно менять как угодно.</p>
        <div className="panel-empty__actions">
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              className="btn btn--primary"
              onClick={() => createBoard(p.key)}
            >
              {p.name}
            </button>
          ))}
          <button type="button" className="btn btn--ghost" onClick={() => createBoard(null)}>
            Пустая панель
          </button>
        </div>
      </div>
    )
  }

  const stale = Boolean(error) && Object.keys(results).length > 0

  return (
    <div className="infopanel">
      <div className="infopanel__bar">
        <div className="infopanel__tabs" role="tablist" aria-label="Панели">
          {boards.map((b) => (
            <button
              key={b.id}
              type="button"
              role="tab"
              aria-selected={b.id === activeId}
              className={`infopanel__tab ${b.id === activeId ? 'is-active' : ''}`}
              onClick={() => setActiveId(b.id)}
            >
              {b.name}
              {!b.can_edit && <span className="infopanel__tab-badge">общая</span>}
            </button>
          ))}
          <button
            type="button"
            className="infopanel__tab infopanel__tab--add"
            aria-label="Создать панель"
            aria-expanded={creating}
            onClick={() => setCreating((v) => !v)}
          >
            <Icon name="plus" size={16} />
          </button>
        </div>

        <div className="infopanel__actions">
          <span className="infopanel__status" aria-live="off">
            {stale
              ? 'данные могли устареть'
              : updatedAt
                ? `обновлено ${relativeTime(updatedAt)}`
                : ''}
          </span>

          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={refresh}
            aria-label="Обновить данные"
          >
            <Icon name="expand" size={16} /> Обновить
          </button>

          {canEdit ? (
            <>
              <button
                type="button"
                className="btn btn--primary btn--sm"
                onClick={() => setGalleryOpen(true)}
              >
                <Icon name="plus" size={16} /> Гаджет
              </button>
              <button
                type="button"
                className="btn btn--ghost btn--sm"
                onClick={() => setSettingsOpen(true)}
              >
                <Icon name="filter" size={16} /> Настройки
              </button>
            </>
          ) : (
            <button type="button" className="btn btn--primary btn--sm" onClick={cloneBoard}>
              Клонировать себе
            </button>
          )}
        </div>
      </div>

      {creating && (
        <div className="infopanel__create">
          <span className="infopanel__create-label">Начать с шаблона</span>
          {presets.map((p) => (
            <button
              key={p.key}
              type="button"
              className="chip"
              title={p.description}
              onClick={() => createBoard(p.key)}
            >
              {p.name}
            </button>
          ))}
          <button type="button" className="chip" onClick={() => createBoard(null)}>
            Пустая панель
          </button>
          <button
            type="button"
            className="btn btn--ghost btn--sm"
            onClick={() => setCreating(false)}
          >
            Отмена
          </button>
        </div>
      )}

      {loadError && <p className="infopanel__error">{loadError}</p>}

      <DashboardGrid
        layout={layout}
        catalog={catalogByType}
        results={results}
        loading={loading}
        updatedAt={updatedAt}
        stale={stale}
        canEdit={canEdit}
        onLayoutChange={updateLayout}
        onSettings={setEditingWidget}
        onRetry={refresh}
      />

      <DisabledTray
        layout={layout}
        catalog={catalogByType}
        canEdit={canEdit}
        onEnable={(id) =>
          updateLayout(layout.map((w) => (w.id === id ? { ...w, enabled: true } : w)))
        }
      />

      {galleryOpen && (
        <WidgetGallery
          catalogItems={catalogItems}
          onAdd={addWidget}
          onClose={() => setGalleryOpen(false)}
        />
      )}

      {editingWidget && (
        <WidgetSettingsModal
          widget={editingWidget}
          spec={catalogByType[editingWidget.type]}
          onClose={() => setEditingWidget(null)}
          onSave={(settings) => {
            updateLayout(
              layout.map((w) => (w.id === editingWidget.id ? { ...w, settings } : w)),
            )
            setEditingWidget(null)
          }}
        />
      )}

      {settingsOpen && (
        <DashboardSettingsModal
          board={board}
          catalogItems={catalogItems}
          canManageCatalog={canManageCatalog}
          onSaveBoard={saveBoardSettings}
          onSaveCatalog={saveCatalog}
          onDelete={deleteBoard}
          onClose={() => setSettingsOpen(false)}
        />
      )}
    </div>
  )
}
